/**
 * Types, preset catalogs and label formatters for per-domain nugget configs.
 *
 * The wire shapes mirror `app/schemas/nugget_config.py`. Because the backend stores `timeframe` and
 * `schedules` in JSON columns with no DB CHECK, the API's Pydantic models are the only validator —
 * so anything built here must round-trip through them cleanly.
 */

export const TIMEFRAME_MODE = {
  ROLLING: "rolling",
  CALENDAR: "calendar",
  ABSOLUTE: "absolute",
} as const;

export type TimeframeMode = (typeof TIMEFRAME_MODE)[keyof typeof TIMEFRAME_MODE];

/**
 * Whether a run's scan targets come from the semantic model's curated candidates.
 *
 * Mirrors `NuggetCandidateSeeding` in `app/db/models/nugget_config.py`. `OFF` is what every
 * config did before the column existed: scan targets come from the deterministic tracking-plan
 * floor plus the intelligence LLM's additions, and curated candidates influence only relationship
 * metadata. `AUGMENT` adds candidate-derived targets on top; `ONLY` replaces the floor with them
 * and drops the LLM's additions.
 */
export const CANDIDATE_SEEDING = {
  OFF: "off",
  AUGMENT: "augment",
  ONLY: "only",
} as const;

export type CandidateSeeding = (typeof CANDIDATE_SEEDING)[keyof typeof CANDIDATE_SEEDING];

/** Render order for the picker — least to most invasive, so `off` reads as the safe default. */
export const CANDIDATE_SEEDING_MODES: readonly CandidateSeeding[] = [
  CANDIDATE_SEEDING.OFF,
  CANDIDATE_SEEDING.AUGMENT,
  CANDIDATE_SEEDING.ONLY,
];

export const TIMEFRAME_UNIT = {
  HOUR: "hour",
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  QUARTER: "quarter",
  YEAR: "year",
} as const;

export type TimeframeUnit = (typeof TIMEFRAME_UNIT)[keyof typeof TIMEFRAME_UNIT];

/**
 * Rolling counts back from run time; calendar aligns to boundaries so has no `hour`.
 *
 * `hour` is absent from both lists: the nugget pipeline resolves windows to whole dates
 * (`TO_DATE(...)` literals, no time-of-day anywhere), so a sub-day window cannot be honoured —
 * "last 6 hours" could only ever mean a whole day. `TIMEFRAME_UNIT.HOUR` is kept so configs saved
 * before it was withdrawn still load; the backend floors them to whole days.
 */
export const ROLLING_UNITS: readonly TimeframeUnit[] = [
  TIMEFRAME_UNIT.DAY,
  TIMEFRAME_UNIT.WEEK,
  TIMEFRAME_UNIT.MONTH,
  TIMEFRAME_UNIT.QUARTER,
  TIMEFRAME_UNIT.YEAR,
];

export const CALENDAR_UNITS: readonly TimeframeUnit[] = [
  TIMEFRAME_UNIT.WEEK,
  TIMEFRAME_UNIT.MONTH,
  TIMEFRAME_UNIT.QUARTER,
  TIMEFRAME_UNIT.YEAR,
];

/** Mirrors `_MAX_AMOUNT_BY_UNIT` in the backend schema; kept in sync so the UI clamps first. */
export const MAX_AMOUNT_BY_UNIT: Record<TimeframeUnit, number> = {
  [TIMEFRAME_UNIT.HOUR]: 720,
  [TIMEFRAME_UNIT.DAY]: 3650,
  [TIMEFRAME_UNIT.WEEK]: 520,
  [TIMEFRAME_UNIT.MONTH]: 120,
  [TIMEFRAME_UNIT.QUARTER]: 40,
  [TIMEFRAME_UNIT.YEAR]: 10,
};

export type RollingTimeframe = {
  mode: typeof TIMEFRAME_MODE.ROLLING;
  unit: TimeframeUnit;
  amount: number;
};

export type CalendarTimeframe = {
  mode: typeof TIMEFRAME_MODE.CALENDAR;
  unit: TimeframeUnit;
  amount: number;
};

export type AbsoluteTimeframe = {
  mode: typeof TIMEFRAME_MODE.ABSOLUTE;
  start_date: string;
  end_date: string;
};

export type NuggetTimeframe = RollingTimeframe | CalendarTimeframe | AbsoluteTimeframe;

export const SCHEDULE_KIND = {
  DAILY: "daily",
  WEEKLY: "weekly",
  MONTHLY: "monthly",
  QUARTERLY: "quarterly",
} as const;

export type ScheduleKind = (typeof SCHEDULE_KIND)[keyof typeof SCHEDULE_KIND];

export const MONTH_DAY_MODE = {
  DAY: "day",
  LAST: "last",
} as const;

export type MonthDayMode = (typeof MONTH_DAY_MODE)[keyof typeof MONTH_DAY_MODE];

export type NuggetSchedule = {
  kind: ScheduleKind;
  interval_count: number;
  /** 0 = Monday … 6 = Sunday, matching Python's `date.weekday()`. Weekly only. */
  day_of_week?: number | null;
  day_of_month?: number | null;
  month_day_mode?: MonthDayMode | null;
  anchor_date?: string | null;
  /** `HH:MM` or `HH:MM:SS` in **UTC** — the backend has no per-tenant timezone handling. */
  run_at_time: string;
  sort_order?: number;
};

export type NuggetConfig = {
  id: number;
  domain_id: number;
  name: string;
  description: string | null;
  focus_areas: string[];
  use_semantic_materiality: boolean;
  candidate_seeding: CandidateSeeding;
  timeframe: NuggetTimeframe;
  schedules: NuggetSchedule[];
  created_at: string;
  updated_at: string;
};

export type NuggetConfigDraft = {
  name: string;
  description: string;
  focus_areas: string[];
  use_semantic_materiality: boolean;
  candidate_seeding: CandidateSeeding;
  timeframe: NuggetTimeframe;
  schedules: NuggetSchedule[];
};

export const DEFAULT_RUN_AT_TIME = "02:00";

export const DEFAULT_TIMEFRAME: NuggetTimeframe = {
  mode: TIMEFRAME_MODE.ROLLING,
  unit: TIMEFRAME_UNIT.DAY,
  amount: 7,
};

export function emptyNuggetConfigDraft(): NuggetConfigDraft {
  return {
    name: "",
    description: "",
    focus_areas: [],
    use_semantic_materiality: true,
    candidate_seeding: CANDIDATE_SEEDING.OFF,
    timeframe: DEFAULT_TIMEFRAME,
    schedules: [],
  };
}

export function draftFromConfig(config: NuggetConfig): NuggetConfigDraft {
  return {
    name: config.name,
    description: config.description ?? "",
    focus_areas: [...config.focus_areas],
    use_semantic_materiality: config.use_semantic_materiality,
    candidate_seeding: config.candidate_seeding,
    timeframe: config.timeframe,
    schedules: config.schedules.map((schedule) => ({ ...schedule })),
  };
}

// ---------------------------------------------------------------------------
// Timeframe presets
// ---------------------------------------------------------------------------

/**
 * The short list shown before `Custom…`. "Last 7 days" and "Last calendar week" are both present
 * on purpose: one is the trailing week, the other the previous Mon–Sun, and they are different
 * windows.
 */
export type TimeframePresetId =
  | "last_1_day"
  | "last_7_days"
  | "last_30_days"
  | "last_calendar_week"
  | "last_calendar_month"
  | "last_calendar_quarter"
  | "last_calendar_year";

export const TIMEFRAME_CUSTOM = "custom" as const;

export type TimeframeSelection = TimeframePresetId | typeof TIMEFRAME_CUSTOM;

export const TIMEFRAME_PRESETS: readonly { id: TimeframePresetId; timeframe: NuggetTimeframe }[] = [
  { id: "last_1_day", timeframe: { mode: "rolling", unit: "day", amount: 1 } },
  { id: "last_7_days", timeframe: { mode: "rolling", unit: "day", amount: 7 } },
  { id: "last_30_days", timeframe: { mode: "rolling", unit: "day", amount: 30 } },
  { id: "last_calendar_week", timeframe: { mode: "calendar", unit: "week", amount: 1 } },
  { id: "last_calendar_month", timeframe: { mode: "calendar", unit: "month", amount: 1 } },
  { id: "last_calendar_quarter", timeframe: { mode: "calendar", unit: "quarter", amount: 1 } },
  { id: "last_calendar_year", timeframe: { mode: "calendar", unit: "year", amount: 1 } },
];

/** Which dropdown entry a stored timeframe corresponds to (`custom` when it matches no preset). */
export function timeframeSelectionOf(timeframe: NuggetTimeframe): TimeframeSelection {
  const match = TIMEFRAME_PRESETS.find((preset) => {
    const candidate = preset.timeframe;
    if (candidate.mode !== timeframe.mode) {
      return false;
    }
    if (candidate.mode === TIMEFRAME_MODE.ABSOLUTE || timeframe.mode === TIMEFRAME_MODE.ABSOLUTE) {
      return false;
    }
    return candidate.unit === timeframe.unit && candidate.amount === timeframe.amount;
  });
  return match?.id ?? TIMEFRAME_CUSTOM;
}

/** Clamp an amount into the unit's allowed range so the UI never posts a doomed value. */
export function clampAmount(unit: TimeframeUnit, amount: number): number {
  if (!Number.isFinite(amount)) {
    return 1;
  }
  return Math.min(Math.max(Math.trunc(amount), 1), MAX_AMOUNT_BY_UNIT[unit]);
}

// ---------------------------------------------------------------------------
// Frequency presets
// ---------------------------------------------------------------------------

export const MONDAY = 0;

/**
 * Presets offered when adding a frequency. `quarterly` is a distinct kind rather than
 * `monthly × 3`: quarterly aligns to calendar quarters (Jan/Apr/Jul/Oct), whereas a 3-month
 * interval is anchor-relative and would give Feb/May/Aug/Nov from a February anchor.
 */
export type SchedulePresetId =
  | "every_day"
  | "every_week"
  | "every_2_weeks"
  | "first_of_month"
  | "last_of_month"
  | "first_of_quarter"
  | "last_of_quarter";

export const SCHEDULE_PRESETS: readonly { id: SchedulePresetId; schedule: NuggetSchedule }[] = [
  {
    id: "every_day",
    schedule: { kind: "daily", interval_count: 1, run_at_time: DEFAULT_RUN_AT_TIME },
  },
  {
    id: "every_week",
    schedule: {
      kind: "weekly",
      interval_count: 1,
      day_of_week: MONDAY,
      run_at_time: DEFAULT_RUN_AT_TIME,
    },
  },
  {
    id: "every_2_weeks",
    schedule: {
      kind: "weekly",
      interval_count: 2,
      day_of_week: MONDAY,
      run_at_time: DEFAULT_RUN_AT_TIME,
    },
  },
  {
    id: "first_of_month",
    schedule: {
      kind: "monthly",
      interval_count: 1,
      day_of_month: 1,
      month_day_mode: MONTH_DAY_MODE.DAY,
      run_at_time: DEFAULT_RUN_AT_TIME,
    },
  },
  {
    id: "last_of_month",
    schedule: {
      kind: "monthly",
      interval_count: 1,
      month_day_mode: MONTH_DAY_MODE.LAST,
      run_at_time: DEFAULT_RUN_AT_TIME,
    },
  },
  {
    id: "first_of_quarter",
    schedule: {
      kind: "quarterly",
      interval_count: 1,
      day_of_month: 1,
      month_day_mode: MONTH_DAY_MODE.DAY,
      run_at_time: DEFAULT_RUN_AT_TIME,
    },
  },
  {
    id: "last_of_quarter",
    schedule: {
      kind: "quarterly",
      interval_count: 1,
      month_day_mode: MONTH_DAY_MODE.LAST,
      run_at_time: DEFAULT_RUN_AT_TIME,
    },
  },
];

/**
 * Rebuild a schedule so its fields are legal for `kind` — the API rejects a weekly entry carrying
 * `day_of_month`, and vice versa, so switching kind has to drop the fields that no longer apply.
 */
export function normalizeScheduleForKind(
  schedule: NuggetSchedule,
  kind: ScheduleKind,
): NuggetSchedule {
  const base = {
    kind,
    interval_count: Math.max(1, schedule.interval_count),
    run_at_time: schedule.run_at_time,
    anchor_date: schedule.anchor_date ?? null,
  };
  if (kind === SCHEDULE_KIND.DAILY) {
    return { ...base, day_of_week: null, day_of_month: null, month_day_mode: null };
  }
  if (kind === SCHEDULE_KIND.WEEKLY) {
    return {
      ...base,
      day_of_week: schedule.day_of_week ?? MONDAY,
      day_of_month: null,
      month_day_mode: null,
    };
  }
  const monthDayMode = schedule.month_day_mode ?? MONTH_DAY_MODE.DAY;
  return {
    ...base,
    day_of_week: null,
    day_of_month: monthDayMode === MONTH_DAY_MODE.LAST ? null : (schedule.day_of_month ?? 1),
    month_day_mode: monthDayMode,
  };
}

/** Duplicate key, matching `NuggetSchedule.identity()` on the server (order is not identity). */
export function scheduleIdentity(schedule: NuggetSchedule): string {
  return [
    schedule.kind,
    schedule.interval_count,
    schedule.day_of_week ?? "",
    schedule.day_of_month ?? "",
    schedule.month_day_mode ?? "",
    normalizeTimeOfDay(schedule.run_at_time),
  ].join("|");
}

export function hasDuplicateSchedules(schedules: NuggetSchedule[]): boolean {
  const seen = new Set<string>();
  for (const schedule of schedules) {
    const key = scheduleIdentity(schedule);
    if (seen.has(key)) {
      return true;
    }
    seen.add(key);
  }
  return false;
}

// ---------------------------------------------------------------------------
// Time-of-day helpers
// ---------------------------------------------------------------------------

/** The API returns `HH:MM:SS`; `<input type="time">` wants `HH:MM`. */
export function normalizeTimeOfDay(value: string): string {
  const [hours = "00", minutes = "00"] = value.split(":");
  return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
}

export function localTimeZoneName(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Render a UTC `HH:MM` in the viewer's own zone. Stored times are UTC, so someone in IST typing
 * 09:00 would actually fire at 14:30 local — showing both is what makes that visible.
 */
export function utcTimeInLocalZone(value: string): string {
  const [hoursRaw, minutesRaw] = normalizeTimeOfDay(value).split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return "";
  }
  const now = new Date();
  const utc = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hours, minutes),
  );
  return utc.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}
